import { type ApiService, API_ROUTES } from '@shared/api-utils';
import { createDebug } from '@shared/debug';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { OfferBundleDetails } from '../types';

// Type guard for HTTP errors
function isHttpError(error: unknown): error is Error & { status: number } {
  return error instanceof Error && 'status' in error && typeof (error as Error & { status: unknown }).status === 'number';
}

type UseOfferDataProps = {
  code?: string;
  iterationNr?: number;
  apiService: ApiService;
};

type UseOfferDataReturn = {
  data: OfferBundleDetails | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

const mapExtraErrors = (offerData: OfferBundleDetails) => {
  if (offerData.lockedByPlatform) {
    offerData.assignationFailureReason = 'BUNDLE_LOCKED_BY_PLATFORM';
  }

  if (offerData.lockedByInvalidAccount) {
    offerData.assignationFailureReason = 'BUNDLE_LOCKED_BY_INVALID_ACCOUNT';
  }

  return offerData;
};

function usePrivateOfferBundleData({ code, iterationNr, apiService }: UseOfferDataProps): UseOfferDataReturn {
  const debug = createDebug({ isEnabled: false });
  const apiServiceRef = useRef(apiService);
  const [offerBundleData, setOfferBundleData] = useState<OfferBundleDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOfferData = useCallback(async () => {
    if (!code) return;

    try {
      setLoading(true);
      setError(null);

      let path = API_ROUTES.OFFER_BUNDLES.PRIVATE.replace(':offerbundleCode', code);
      if (Number.isInteger(iterationNr)) {
        const iterationNrString = `?iterationNr=${iterationNr}`;
        path = path + iterationNrString;
      }

      const response = await apiServiceRef.current.get<OfferBundleDetails>(path, {
        credentials: 'include',
      });

      if (response.error) {
        throw response.error;
      }

      if (!response.data) {
        setError('No offer data received');
        return;
      }

      const nextOfferBundleData = mapExtraErrors(response.data);
      setOfferBundleData(nextOfferBundleData);
    } catch (err) {
      const httpError = err as Error & { status?: number };

      if (isHttpError(httpError) && httpError.status === 404) {
        try {
          const assignationPath = API_ROUTES.OFFER_BUNDLES.ASSIGNATION.replace(':offerbundleCode', code);
          const assignationResponse = await apiServiceRef.current.post<OfferBundleDetails>(assignationPath, undefined, {
            credentials: 'include',
          });

          if (!assignationResponse.data) {
            setError('Failed to assign offer');
            return;
          }

          if (assignationResponse.data.lockedByPlatform) {
            assignationResponse.data.assignationFailureReason = 'BUNDLE_LOCKED_BY_PLATFORM';
          }

          if (assignationResponse.data.lockedByInvalidAccount) {
            assignationResponse.data.assignationFailureReason = 'BUNDLE_LOCKED_BY_INVALID_ACCOUNT';
          }

          const nextOfferBundleData = mapExtraErrors(assignationResponse.data);
          setOfferBundleData(nextOfferBundleData);
        } catch (assignationError) {
          setError(assignationError instanceof Error ? assignationError.message : 'Failed to assign offer');
        }
      } else {
        setError(httpError.message || 'Failed to fetch offer data');
      }
    } finally {
      setLoading(false);
    }
  }, [code, iterationNr, apiServiceRef]);

  const markAsVisited = useCallback(async () => {
    const currentData = offerBundleData;

    if (!currentData || currentData.assignationFailureReason || currentData.visited) {
      return;
    }

    const path = API_ROUTES.OFFER_BUNDLES.VISITED.replace(':bundleId', currentData.bundleId).replace(':iterationNr', currentData.iterationNr?.toString());

    const result = await Promise.resolve(
      apiServiceRef.current.patch(path, undefined, {
        credentials: 'include',
        keepalive: true,
        retry: { attempts: 2 },
      })
    ).catch(error => ({
      data: null,
      error: error instanceof Error ? error : new Error(String(error)),
    }));

    if (result?.error) {
      debug.forceError('Failed to mark offer bundle as visited', { bundleId: currentData.bundleId, iterationNr: currentData.iterationNr, error: result.error });
    }

    // 'debug' is intentionally excluded — it's recreated every render and only used for logging.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerBundleData, apiServiceRef]);

  useEffect(() => {
    fetchOfferData();
  }, [code, iterationNr, fetchOfferData]);

  useEffect(() => {
    if (offerBundleData && !loading) {
      markAsVisited();
    }
  }, [offerBundleData, loading, markAsVisited]);

  return { data: offerBundleData, loading, error, refetch: fetchOfferData };
}

export { usePrivateOfferBundleData };
